import { useCallback, useEffect, useMemo, useRef } from "react";
import type { IHubProfileContext } from "@/application/ports/IHubProfileContext";
import { useHubProfiles } from "@/application/queries";
import { useHubProfileStore } from "@/stores/useHubProfileStore";
import { getHubProfileStorageService, getLogger } from "@/infrastructure/di/services";

const isBrowser = typeof window !== "undefined";

/**
 * Hub profile composition hook.
 * Combines Zustand UI state with TanStack Query server state.
 */
export const useHubProfile = (): IHubProfileContext => {
  const log = getLogger();
  const hubStorage = getHubProfileStorageService();

  // UI state from Zustand
  const selectedProfileUuid = useHubProfileStore((s) => s.selectedProfileUuid);
  const isSwitchingProfile = useHubProfileStore((s) => s.isSwitchingProfile);
  const setSelectedProfile = useHubProfileStore((s) => s.setSelectedProfile);
  const setIsSwitchingProfile = useHubProfileStore((s) => s.setIsSwitchingProfile);
  const clearSwitchingState = useHubProfileStore((s) => s.clearSwitchingState);

  // Server state from TanStack Query
  const { data, isLoading, error: queryError } = useHubProfiles();

  const profiles = useMemo(() => data?.profiles ?? [], [data?.profiles]);

  const errorMessage = queryError
    ? queryError instanceof Error
      ? queryError.message
      : "Failed to load profiles"
    : profiles.length === 0 && !isLoading
      ? "No investor profiles found. Please contact your organization administrator."
      : null;

  // Track whether we've auto-selected a profile after initial load
  const hasAutoSelectedRef = useRef(false);

  // Single primitive used by the auto-select effect so its body only fires
  // one updater (rule 06.SU5).
  const commitProfile = useCallback(
    (uuid: string, errorContext: string) => {
      setSelectedProfile(uuid);
      if (!isBrowser) return;
      try {
        hubStorage.setSelectedProfileUuid(uuid);
      } catch (error) {
        log.debug(errorContext, { error });
      }
    },
    [setSelectedProfile, hubStorage, log],
  );

  // Auto-select first profile if none is selected, or validate current selection
  useEffect(() => {
    if (isLoading || profiles.length === 0) return;
    if (hasAutoSelectedRef.current) return;

    if (!selectedProfileUuid) {
      commitProfile(
        profiles[0].uuid,
        "localStorage unavailable when auto-selecting first hub profile",
      );
    } else if (!profiles.some((p) => p.uuid === selectedProfileUuid)) {
      commitProfile(
        profiles[0].uuid,
        "localStorage unavailable when replacing invalid hub profile",
      );
    }
    hasAutoSelectedRef.current = true;
  }, [isLoading, profiles, selectedProfileUuid, commitProfile]);

  const selectProfile = useCallback(
    (uuid: string) => {
      const profile = profiles.find((p) => p.uuid === uuid);
      if (!profile || uuid === selectedProfileUuid) return;

      setIsSwitchingProfile(true);
      setSelectedProfile(uuid);

      if (isBrowser) {
        try {
          hubStorage.setSelectedProfileUuid(uuid);
        } catch (error) {
          log.debug("localStorage unavailable when persisting profile selection", { error });
        }
      }
    },
    [profiles, selectedProfileUuid, setIsSwitchingProfile, setSelectedProfile, hubStorage, log],
  );

  const selectedProfile = useMemo(() => {
    if (!selectedProfileUuid) return null;
    return profiles.find((p) => p.uuid === selectedProfileUuid) ?? null;
  }, [profiles, selectedProfileUuid]);

  const hasMultipleProfiles = profiles.length > 1;

  return useMemo<IHubProfileContext>(
    () => ({
      profiles,
      hasMultipleProfiles,
      selectedProfile,
      selectedProfileUuid,
      isLoading,
      isSwitchingProfile,
      error: errorMessage,
      selectProfile,
      clearSwitchingState,
    }),
    [
      profiles,
      hasMultipleProfiles,
      selectedProfile,
      selectedProfileUuid,
      isLoading,
      isSwitchingProfile,
      errorMessage,
      selectProfile,
      clearSwitchingState,
    ],
  );
};
